import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import Homepage from './components/Homepage';
import AirdropLoot from './components/AirdropLoot';
import ConnectWallet from './components/ConnectWallet';
import Roadmap from './components/Roadmap';
import Features from './components/Features';
import AllSections from './components/AllSections';
import ClaimSuccess from './components/ClaimSuccess';
import Nav from './components/Nav';
import Footer from './components/Footer';
import JoinEarlyBox from './components/JoinEarlyBox';
import MessageBox from './components/MessageBox';

import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import AboutUs from './pages/AboutUs';
import TradingDashboard from './pages/dashboard/TradingDashboard';
import BotActivation from './pages/BotActivation';

import './App.css';

// ✅ Keep scroll ALWAYS unlocked (styles-level)
const forceUnlockScroll = () => {
  document.body.style.overflow = 'auto';
  document.body.style.position = 'static';
  document.body.style.width = 'auto';
  document.body.style.height = 'auto';

  document.documentElement.style.overflow = 'auto';
  // IMPORTANT: keep global scroll-behavior auto to avoid wheel jank
  document.documentElement.style.scrollBehavior = 'auto';
};

// ✅ Custom scroll to hash hook
const useScrollToHash = () => {
  const location = useLocation();

  useEffect(() => {
    forceUnlockScroll();

    const timer = setTimeout(() => {
      if (location.hash) {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [location]);
};

function App() {
  useScrollToHash();

  // ✅ Ensure scroll is never locked (mount/unmount safety)
  useEffect(() => {
    forceUnlockScroll();
    return () => forceUnlockScroll();
  }, []);

  // ✅ FIX: Wheel/Touchpad scroll was blocked (worked only via scrollbar drag)
  // ✅ IMPROVED: rAF throttled manual scroll (smooth, no atak)
  useEffect(() => {
    const isAnyWalletModalOpen = () => {
      return Boolean(
        document.querySelector(
          [
            'w3m-modal',
            "[data-testid='w3m-modal']",
            '.w3m-modal',
            '.w3m-overlay',
            '.wui-overlay',
            '.wui-modal',
            '.modal',
            '.modal-overlay',
            '.popup-overlay',
          ].join(',')
        )
      );
    };

    let rafId = null;
    let pendingDelta = 0;

    const flush = () => {
      rafId = null;

      if (pendingDelta !== 0) {
        window.scrollBy({ top: pendingDelta, left: 0, behavior: 'auto' });
        pendingDelta = 0;
      }
    };

    const scheduleScroll = (deltaY) => {
      if (!deltaY) return;

      // ✅ Clamp spikes (touchpad can send very large deltas)
      const clamped = Math.max(-180, Math.min(180, deltaY));

      pendingDelta += clamped;

      if (rafId == null) {
        rafId = requestAnimationFrame(flush);
      }
    };

    const onWheel = (e) => {
      // If wallet/modal open, don't interfere
      if (isAnyWalletModalOpen()) return;

      // Don't interfere inside slide menu
      const inMenu = e.target?.closest?.('.slide-menu');
      if (inMenu) return;

      // ✅ Take control early (bypass blocker listeners)
      try {
        e.stopImmediatePropagation();
        e.preventDefault();
      } catch (_) {}

      scheduleScroll(e.deltaY);
    };

    let lastTouchY = 0;

    const onTouchStart = (e) => {
      lastTouchY = e.touches?.[0]?.clientY ?? 0;
    };

    const onTouchMove = (e) => {
      if (isAnyWalletModalOpen()) return;

      const inMenu = e.target?.closest?.('.slide-menu');
      if (inMenu) return;

      const y = e.touches?.[0]?.clientY ?? 0;
      const delta = lastTouchY - y;
      lastTouchY = y;

      try {
        e.stopImmediatePropagation();
        e.preventDefault();
      } catch (_) {}

      scheduleScroll(delta);
    };

    // capture + passive:false so preventDefault works
    window.addEventListener('wheel', onWheel, { capture: true, passive: false });
    window.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    window.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });

    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('touchstart', onTouchStart, { capture: true });
      window.removeEventListener('touchmove', onTouchMove, { capture: true });

      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
      pendingDelta = 0;
    };
  }, []);

  return (
    <div className="app-container">
      <Nav />

      <main>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Homepage />
                <JoinEarlyBox />
                <AirdropLoot />
                <ConnectWallet />
                <Roadmap />
                <Features />
              </>
            }
          />

          <Route
            path="/airdrop"
            element={
              <>
                <AirdropLoot />
                <MessageBox
                  icon="🚀"
                  text="VORTREX isn't just a token – it's a revolution in passive income"
                />
              </>
            }
          />

          <Route
            path="/connect-wallet"
            element={
              <>
                <JoinEarlyBox message="🔐 Connect Your Wallet to Unlock Passive Income Power!" />
                <ConnectWallet />
              </>
            }
          />

          <Route
            path="/roadmap"
            element={
              <>
                <JoinEarlyBox message="🌊 While others trade daily, VORTREX holders earn daily." />
                <Roadmap />
              </>
            }
          />

          <Route path="/features" element={<Features />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/all" element={<AllSections />} />
          <Route path="/claim-success" element={<ClaimSuccess />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />

          {/* ✅ Bot Activation Route */}
          <Route path="/bot-activation" element={<BotActivation />} />

          {/* ✅ Nested Dashboard Route */}
          <Route path="/dashboard/*" element={<TradingDashboard />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

export default App;
